import React from "react";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import RoomIcon from "@mui/icons-material/Room";
import WavesIcon from "@mui/icons-material/Waves";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import TableChartIcon from "@mui/icons-material/TableChart";
import MapIcon from "@mui/icons-material/Map";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import PoolChecklist from "./PoolChecklist";
import '../css/custom-datepicker.css';

export default function MapFilter({ 
  settings, 
  onFilterChange, 
  onDateChange, 
  startDate: propStartDate, 
  endDate: propEndDate, 
  onPoolSelection, 
  pools,
  loading = false,
  loadingProgress = 0,
  onToggleDataView,
  showDataView = false,
  totalCO2,
  totalElectricity
}) {
  const handleStartDateChange = (date) => {
    onFilterChange('startDate', date);
    onDateChange(date, propEndDate);
  };

  const handleEndDateChange = (date) => {
    onFilterChange('endDate', date);
    onDateChange(propStartDate, date);
  };

  const handleTypeChange = () => {
    onFilterChange("type", settings.type === "electricity" ? "carbon" : "electricity");
  };

  const label = { inputProps: { "aria-label": "Switch demo" } };

  const minDate = new Date(2021, 0, 1, 0, 0, 0, 0);
  const maxDate = (() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  })();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}>
        {/* Loading Progress Bar - Removed to avoid duplicate loading states in data view */}

        <Grid container sx={{ zIndex: 1000 }} className="bg-red-600 p-2 shadow" alignItems="center" spacing={1}>
          {/* Left side controls - grouped closer together */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={1.5} alignItems="flex-end">
              {/* Map/Data View Toggle */}
              <Grid item xs={12} sm={3} md={2} sx={{ minWidth: '80px' }}>
                <Tooltip title={showDataView ? 'Switch to Map View' : 'Switch to Data View'}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onToggleDataView}
                    startIcon={showDataView ? <MapIcon /> : <TableChartIcon />}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.4rem',
                      height: '1.5rem',
                      width: '100%',
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    {showDataView ? 'Map' : 'Data'}
                  </Button>
                </Tooltip>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={4.5} md={2.5} sx={{ minWidth: '140px' }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: '600', fontSize: '0.65rem', mb: 0.25, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  START DATE
                </Typography>
                <ReactDatePicker
                  selected={propStartDate}
                  onChange={handleStartDateChange}
                  selectsStart
                  startDate={propStartDate}
                  endDate={propEndDate}
                  className="form-control"
                  minDate={minDate}
                  maxDate={maxDate}
                  placeholderText="Start Date"
                  wrapperClassName="date-picker-wrapper"
                  dateFormat="MMM dd, yyyy"
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  popperModifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={4.5} md={2.5} sx={{ minWidth: '140px' }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: '600', fontSize: '0.65rem', mb: 0.25, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  END DATE
                </Typography>
                <ReactDatePicker
                  selected={propEndDate}
                  onChange={handleEndDateChange}
                  selectsEnd
                  startDate={propStartDate}
                  endDate={propEndDate}
                  className="form-control"
                  minDate={minDate}
                  maxDate={maxDate}
                  placeholderText="End Date"
                  wrapperClassName="date-picker-wrapper"
                  dateFormat="MMM dd, yyyy"
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  popperModifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Control Switches and PoolChecklist */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
              {/* Markers Switch */}
              <Grid item>
                <Grid container alignItems="center" spacing={0.5}>
                  <Grid item>
                    <Switch
                      {...label}
                      checked={settings.markersOn}
                      onChange={() => onFilterChange("markersOn", !settings.markersOn)}
                      color="green"
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <RoomIcon className={settings.markersOn ? "text-neutral-200" : "text-gray-500"} />
                  </Grid>
                </Grid>
              </Grid>

              {/* Vertical Divider */}
              <Grid item>
                <Divider orientation="vertical" flexItem sx={{ height: '2rem' }} />
              </Grid>

              {/* Heatmap Switch */}
              <Grid item>
                <Grid container alignItems="center" spacing={0.5}>
                  <Grid item>
                    <Switch
                      {...label}
                      checked={settings.heatmapOn}
                      onChange={() => onFilterChange("heatmapOn", !settings.heatmapOn)}
                      color="warning"
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <WavesIcon className={settings.heatmapOn ? "text-neutral-200" : "text-gray-500"} />
                  </Grid>
                </Grid>
              </Grid>

              {/* Vertical Divider */}
              <Grid item>
                <Divider orientation="vertical" flexItem sx={{ height: '2rem' }} />
              </Grid>

              {/* Type Selection Switch */}
              <Grid item>
                <Grid container alignItems="center" spacing={0.5}>
                  <Grid item>
                    <Typography
                      sx={{ 
                        color: 'yellow', 
                        fontWeight: 'bold', 
                        fontSize: '0.75rem',
                        lineHeight: 1
                      }}
                    >
                      CO<sub>2</sub>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Switch
                      checked={settings.type === "electricity"}
                      onChange={handleTypeChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <ElectricBoltIcon className={settings.type === "electricity" ? "text-blue-500" : "text-gray-500"} />
                  </Grid>
                </Grid>
              </Grid>

              {/* Vertical Divider */}
              <Grid item>
                <Divider orientation="vertical" flexItem sx={{ height: '2rem' }} />
              </Grid>

              {/* PoolChecklist Component */}
              <Grid item sx={{ position: 'relative', zIndex: 1000 }}>
                <PoolChecklist onPoolSelection={onPoolSelection} pools={pools} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
